/**
 * Meta Pixel (navegador) da loja Miahma.
 * - Carrega uma única vez, só na loja pública (miahma.com.br / www) e só com
 *   consentimento de marketing explícito (aviso de cookies → "Aceitar").
 * - Configuração vem de admin_settings.meta_pixel ({ enabled, pixel_id }).
 * - PageView manual por rota (pushState automático do Pixel desligado);
 *   autoConfig desligado para o Pixel não coletar dados de formulários.
 * - Nunca envia evento em páginas com códigos privados no endereço.
 */
import { supabase } from "@/integrations/supabase/client";

export const MARKETING_CONSENT_KEY = "miahma_lgpd_consent";
export const MARKETING_CONSENT_EVENT = "miahma_marketing_consent_changed";
const PURCHASE_SENT_KEY = "miahma-meta-purchase-sent";

type Fbq = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  push?: unknown;
  loaded?: boolean;
  version?: string;
  disablePushState?: boolean;
};
declare global {
  interface Window { fbq?: Fbq; _fbq?: Fbq }
}

const STORE_HOSTS = new Set(["miahma.com.br", "www.miahma.com.br"]);
/** Páginas com token/segredo no endereço ou fora da loja: nada de Pixel. */
const BLOCKED_PATH = /^\/(admin|avaliar|pagar|pagar-complemento|reset-password|newsletter|unsubscribe|descadastrar|ouvidoria|vagas|catalogo|tela-promo|cashback)(\/|$)/i;
const BLOCKED_QUERY = /[?&](token|t|c|k|code|codigo|access_token)=/i;

export type MetaContent = { id: string; quantity: number; item_price?: number };

export function hasMarketingConsent(): boolean {
  try {
    const raw = localStorage.getItem(MARKETING_CONSENT_KEY);
    if (!raw) return false;
    const c = JSON.parse(raw) as { marketing?: boolean };
    return c.marketing === true;
  } catch {
    return false;
  }
}

export function isPixelAllowedHere(loc: { hostname: string; pathname: string; search: string } = window.location): boolean {
  if (!STORE_HOSTS.has(loc.hostname)) return false;
  if (BLOCKED_PATH.test(loc.pathname)) return false;
  if (BLOCKED_QUERY.test(loc.search)) return false;
  if (typeof navigator !== "undefined" && navigator.webdriver) return false;
  return true;
}

let config: { enabled: boolean; pixelId: string } | null = null;
let configPromise: Promise<void> | null = null;
let initialized = false;

export function __setMetaPixelConfigForTests(c: { enabled: boolean; pixelId: string } | null, wait?: Promise<void>) {
  config = c; configPromise = c ? (wait ?? Promise.resolve()) : null; initialized = false;
}

function loadConfig(): Promise<void> {
  if (!configPromise) {
    configPromise = (async () => {
      try {
        const { data } = await supabase.from("admin_settings").select("value").eq("key", "meta_pixel").maybeSingle();
        const v = (data?.value ?? {}) as { enabled?: boolean; pixel_id?: string };
        const id = String(v.pixel_id ?? "").trim();
        config = { enabled: v.enabled === true && /^\d{6,20}$/.test(id), pixelId: id };
      } catch {
        config = { enabled: false, pixelId: "" };
      }
    })();
  }
  return configPromise;
}

function installBase(pixelId: string) {
  if (window.fbq) return; // já instalado: nunca duplica
  const n: Fbq = function (...args: unknown[]) {
    if (n.callMethod) n.callMethod.apply(n, args);
    else n.queue!.push(args);
  } as Fbq;
  n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
  n.disablePushState = true; // PageView da navegação é enviado manualmente
  window.fbq = n;
  if (!window._fbq) window._fbq = n;
  const s = document.createElement("script");
  s.async = true;
  s.src = "https://connect.facebook.net/en_US/fbevents.js";
  document.head.appendChild(s);
  n("set", "autoConfig", false, pixelId);
  n("init", pixelId, {}, { agent: "lovable" });
}

const canTrackNow = () => isPixelAllowedHere() && hasMarketingConsent();

/** Garante o Pixel pronto; false quando não pode/deve rastrear agora. */
async function ready(): Promise<boolean> {
  if (typeof window === "undefined") return false;
  if (!canTrackNow()) return false;
  await loadConfig();
  // reconfere ANTES de instalar/conceder: recusa ou troca de rota durante a espera
  if (!config?.enabled || !canTrackNow()) return false;
  if (!initialized) {
    installBase(config.pixelId);
    initialized = true;
  } else {
    window.fbq?.("consent", "grant");
  }
  return true;
}

/** true só quando o evento foi de fato entregue ao Pixel. */
async function send(event: string, params?: Record<string, unknown>, eventID?: string): Promise<boolean> {
  try {
    if (!(await ready())) return false;
    if (!canTrackNow()) return false;
    window.fbq?.("track", event, params ?? {}, eventID ? { eventID } : undefined);
    return true;
  } catch { return false; /* rastreamento nunca quebra a loja */ }
}

/**
 * Eventos de contexto da página atual (ViewContent/InitiateCheckout) que não puderam
 * sair por falta de aceite ficam guardados SÓ para a rota atual; ao aceitar, saem uma vez.
 * AddToCart nunca é guardado (ação passada sem aceite não é reenviada).
 */
type Pending = { key: string; path: string; event: string; params: Record<string, unknown> };
const pendingContext = new Map<string, Pending>();
const sentContext = new Set<string>();
const inflightContext = new Set<string>();
const currentPath = () => (typeof window === "undefined" ? "" : window.location.pathname);

async function sendContext(key: string, event: string, params: Record<string, unknown>) {
  if (sentContext.has(key) || inflightContext.has(key)) return;
  const path = currentPath();
  inflightContext.add(key);
  try {
    const ok = await send(event, params);
    if (ok) { sentContext.add(key); pendingContext.delete(key); }
    else if (isPixelAllowedHere() && !hasMarketingConsent()) pendingContext.set(key, { key, path, event, params });
  } finally { inflightContext.delete(key); }
}

/** Chamar quando o visitante aceitar: envia só o contexto ainda pendente da página atual. */
export function metaFlushPendingContext() {
  const path = currentPath();
  for (const p of [...pendingContext.values()]) {
    if (p.path !== path) { pendingContext.delete(p.key); continue; }
    void sendContext(p.key, p.event, p.params);
  }
}

export function __resetMetaPixelStateForTests() {
  pendingContext.clear(); sentContext.clear(); inflightContext.clear();
  lastPageView = null; inflightPageView = null; purchaseInflight.clear(); searchInflightOrSent.clear();
}

let lastPageView: string | null = null;
let inflightPageView: string | null = null;
/** Deduplica só depois de envio elegível: sem aceite, a rota não fica marcada. */
export async function metaPageView(path: string): Promise<void> {
  if (lastPageView === path || inflightPageView === path) return;
  inflightPageView = path;
  try {
    if (await send("PageView")) lastPageView = path;
  } finally { if (inflightPageView === path) inflightPageView = null; }
}

const brl = (v: number) => Math.round(v * 100) / 100;

/** Identificador de conteúdo: variante de cor quando houver, senão o produto. */
export const metaContentId = (productId: string, colorVariantId?: string | null) => colorVariantId || productId;

/** Linha legível (parâmetro personalizado `line_items`): mesmo id do contents + nome e cor reais; cor ausente fica ausente. */
export type MetaLine = { id: string; name?: string; color?: string };
const clean = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
export function metaLine(id: string, name?: unknown, color?: unknown): MetaLine {
  const n = clean(name), c = clean(color);
  return { id, ...(n ? { name: n } : {}), ...(c ? { color: c } : {}) };
}
/** "Nome — Cor" só quando a cor real existe. */
export const metaLabel = (name: string, color?: string | null) => { const c = clean(color); return c ? `${name} — ${c}` : name; };
const linesParam = (lines?: MetaLine[]) => (lines && lines.length ? { line_items: lines } : {});

/** `openId`: uma abertura do produto; nova visita (novo openId) envia de novo, re-render da mesma abertura não. */
export function metaViewContent(p: { productId: string; colorVariantId?: string | null; name: string; colorName?: string | null; price: number; openId?: string }) {
  if (!Number.isFinite(p.price)) return;
  const id = metaContentId(p.productId, p.colorVariantId);
  const color = clean(p.colorName);
  void sendContext(`vc:${currentPath()}:${id}:${p.openId ?? ""}`, "ViewContent", { content_ids: [id], content_type: "product", content_name: metaLabel(p.name, color), ...(color ? { color } : {}), value: brl(p.price), currency: "BRL" });
}

export function metaAddToCart(p: { productId: string; colorVariantId?: string | null; name: string; colorName?: string | null; unitPrice: number; quantity: number }) {
  if (!(p.quantity > 0) || !Number.isFinite(p.unitPrice)) return;
  const id = metaContentId(p.productId, p.colorVariantId);
  const color = clean(p.colorName);
  void send("AddToCart", {
    content_ids: [id], content_type: "product", content_name: metaLabel(p.name, color), ...(color ? { color } : {}),
    contents: [{ id, quantity: p.quantity, item_price: brl(p.unitPrice) }],
    value: brl(p.unitPrice * p.quantity), currency: "BRL",
  });
}

export function metaInitiateCheckout(p: { contents: MetaContent[]; value: number; lines?: MetaLine[] }) {
  if (!p.contents.length || !Number.isFinite(p.value) || p.value <= 0) return;
  void sendContext(`ic:${currentPath()}`, "InitiateCheckout", {
    content_ids: p.contents.map((c) => c.id), content_type: "product", contents: p.contents,
    num_items: p.contents.reduce((a, c) => a + c.quantity, 0), value: brl(p.value), currency: "BRL", ...linesParam(p.lines),
  });
}

function purchaseSent(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(PURCHASE_SENT_KEY) || "[]") as string[]); } catch { return new Set(); }
}

/**
 * Purchase: chamar SOMENTE com pagamento confirmado pelo servidor (kind "paid"/"paid_review").
 * Uma vez por pedido neste navegador (sobrevive a reload) + eventID estável para a Meta deduplicar.
 */
const purchaseInflight = new Set<string>();
export async function metaPurchaseOnce(p: { orderId: string; value: number; contents: MetaContent[]; lines?: MetaLine[] }) {
  // dados incompletos (total 0/ausente ou sem itens) esperam: nunca envia valor inventado
  if (!p.orderId || !Number.isFinite(p.value) || p.value <= 0 || p.contents.length === 0) return;
  if (purchaseSent().has(p.orderId) || purchaseInflight.has(p.orderId)) return;
  purchaseInflight.add(p.orderId);
  try { await purchaseAfterReady(p); } finally { purchaseInflight.delete(p.orderId); }
}

async function purchaseAfterReady(p: { orderId: string; value: number; contents: MetaContent[]; lines?: MetaLine[] }) {
  if (!(await ready()) || !canTrackNow()) return;
  const again = purchaseSent();
  if (again.has(p.orderId)) return;
  again.add(p.orderId);
  try { localStorage.setItem(PURCHASE_SENT_KEY, JSON.stringify([...again].slice(-50))); } catch { /* ignore */ }
  window.fbq?.("track", "Purchase", {
    content_ids: p.contents.map((c) => c.id), content_type: "product", contents: p.contents,
    num_items: p.contents.reduce((a, c) => a + c.quantity, 0), value: brl(p.value), currency: "BRL", ...linesParam(p.lines),
  }, { eventID: `purchase:${p.orderId}` });
}

/** Recusa/retirada: para envios imediatamente, sem recarregar. */
/**
 * Texto de busca seguro para a Meta: normaliza espaços, limita tamanho e descarta buscas
 * que parecem dado pessoal digitado por engano (e-mail, CPF, telefone, sequência longa de números).
 * null = não enviar.
 */
export function metaSafeSearchString(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const s = raw.replace(/\s+/g, " ").trim();
  if (s.length < 2) return null;
  if (/[^\s@]+@[^\s@]+/.test(s)) return null; // e-mail
  if ((s.match(/\d/g) || []).length >= 8) return null; // CPF/telefone/CEP/cartão
  return s.slice(0, 100);
}

const searchInflightOrSent = new Set<string>();
/**
 * Search: uma pesquisa realizada (resultados exibidos, inclusive zero). `searchId` identifica
 * a pesquisa; re-render/refetch da mesma pesquisa não reenvia, nova pesquisa (novo id) envia.
 * Ação pontual: sem aceite não é guardada nem reenviada depois.
 */
export function metaSearch(p: { searchString: string; searchId: string }) {
  const s = metaSafeSearchString(p.searchString);
  if (!s) return;
  const key = `${p.searchId}:${s.toLowerCase()}`;
  if (searchInflightOrSent.has(key)) return;
  searchInflightOrSent.add(key);
  // marcado mesmo sem envio: uma pesquisa feita sem aceite nunca é reenviada depois
  void send("Search", { search_string: s, content_type: "product" });
}

export function metaRevokeConsent() {
  try { window.fbq?.("consent", "revoke"); } catch { /* ignore */ }
}
